import { fetchApi, parseError } from '@/api/http'
import { markProfissionalSessionActive, type ProfissionalSession } from '@/utils/profissionalAuth'
import { forceRelogin } from '@/utils/authFlow'

async function fetchProf(path: string, options?: RequestInit): Promise<Response> {
  return fetchApi(path, options)
}

async function handleProfResponse<T>(res: Response): Promise<T> {
  const text = await res.text()
  if (res.status === 401 || res.status === 403) {
    const msg = (() => {
      try {
        const d = JSON.parse(text) as { error?: string }
        return d?.error ?? 'Sessão do profissional inválida ou expirada. Faça login novamente.'
      } catch {
        return 'Sessão do profissional inválida ou expirada. Faça login novamente.'
      }
    })()
    forceRelogin('profissional', msg)
  }
  if (!res.ok) return parseError(res, text)
  if (!text) return undefined as T
  markProfissionalSessionActive()
  return JSON.parse(text) as T
}

export interface ProfissionalLoginResponse {
  profissional: ProfissionalSession
}

export type ProfissionalAgendamentoPeriod = 'proximos' | 'passados' | 'semana' | string

export interface ProfissionalAgendamento {
  id: number
  usuario_id: number
  cliente_nome: string | null
  cliente_email?: string | null
  cliente_telefone?: string | null
  data: string
  hora: string
  profissional: string
  unidade: string
  status: string
  servico_id?: number | null
  servico_nome?: string | null
  observacao?: string | null
}

export interface ProfissionalSemanaDia {
  data: string
  total: number
}

export interface ProfissionalNotificacao {
  id: number
  tipo: string
  titulo: string
  mensagem: string
  agendamento_id: number | null
  lida: number
  criada_em: string
}

export interface ProfissionalMe extends ProfissionalSession {
  unidade_id: number | null
  unidade_nome: string | null
}

export async function loginProfissional(email: string, senha: string): Promise<ProfissionalLoginResponse> {
  const res = await fetchApi('/api/profissional/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email: email.trim(), senha }),
  })
  const text = await res.text()
  if (!res.ok) return parseError(res, text)
  markProfissionalSessionActive()
  return JSON.parse(text) as ProfissionalLoginResponse
}

export async function getProfissionalMe(): Promise<ProfissionalMe> {
  const res = await fetchProf('/api/profissional/me')
  return handleProfResponse<ProfissionalMe>(res)
}

export async function updateProfissionalMe(data: {
  nome?: string
  especialidade?: string
}): Promise<ProfissionalMe> {
  const res = await fetchProf('/api/profissional/me', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  return handleProfResponse<ProfissionalMe>(res)
}

export async function updateProfissionalPassword(
  senhaAtual: string,
  senhaNova: string
): Promise<{ ok: boolean }> {
  const res = await fetchProf('/api/profissional/password', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ senhaAtual, senhaNova }),
  })
  return handleProfResponse<{ ok: boolean }>(res)
}

export async function getProfissionalAgendamentos(
  limit = 100,
  period: ProfissionalAgendamentoPeriod = 'proximos'
): Promise<ProfissionalAgendamento[]> {
  const q = new URLSearchParams({ limit: String(limit), period })
  const res = await fetchProf(`/api/profissional/agendamentos?${q}`)
  return handleProfResponse<ProfissionalAgendamento[]>(res)
}

export async function getProfissionalAgendamentosSemana(): Promise<ProfissionalSemanaDia[]> {
  const res = await fetchProf('/api/profissional/agendamentos/semana')
  return handleProfResponse<ProfissionalSemanaDia[]>(res)
}

export async function updateProfissionalAgendamentoStatus(
  agendamentoId: number,
  status: 'agendado' | 'realizado' | 'cancelado',
  motivo?: string
): Promise<{ id: number; status: string }> {
  const res = await fetchProf(`/api/profissional/agendamentos/${agendamentoId}/status`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status, motivo: motivo?.trim() || undefined }),
  })
  return handleProfResponse<{ id: number; status: string }>(res)
}

export async function getProfissionalNotificacoes(limit = 20): Promise<ProfissionalNotificacao[]> {
  const res = await fetchProf(`/api/profissional/notificacoes?limit=${limit}`)
  return handleProfResponse<ProfissionalNotificacao[]>(res)
}

export async function getProfissionalNotificacoesUnreadCount(): Promise<number> {
  const res = await fetchProf('/api/profissional/notificacoes/unread-count')
  const data = await handleProfResponse<{ unread?: number }>(res)
  return Number(data?.unread || 0)
}

export async function marcarProfissionalNotificacaoLida(id: number): Promise<ProfissionalNotificacao> {
  const res = await fetchProf(`/api/profissional/notificacoes/${id}/lida`, { method: 'PATCH' })
  return handleProfResponse<ProfissionalNotificacao>(res)
}

export async function marcarTodasProfissionalNotificacoesLidas(): Promise<{ updated: number }> {
  const res = await fetchProf('/api/profissional/notificacoes/marcar-todas-lidas', { method: 'PATCH' })
  return handleProfResponse<{ updated: number }>(res)
}

export interface GoogleCalendarStatus {
  connected: boolean
  email: string | null
  configured: boolean
}

export async function getProfissionalGoogleStatus(): Promise<GoogleCalendarStatus> {
  const res = await fetchProf('/api/profissional/google/status')
  return handleProfResponse<GoogleCalendarStatus>(res)
}

export async function getProfissionalGoogleConnectUrl(): Promise<string> {
  const res = await fetchProf('/api/profissional/google/connect')
  const data = await handleProfResponse<{ url: string }>(res)
  if (!data?.url) throw new Error('URL Google em falta.')
  return data.url
}

export async function disconnectProfissionalGoogle(): Promise<void> {
  const res = await fetchProf('/api/profissional/google/disconnect', { method: 'POST' })
  await handleProfResponse<{ ok: boolean }>(res)
}
